/**
 * The calendar and time popups a date field opens.
 *
 * Dawid ruled on 2026-08-04 (R5) that the date and time pickers become
 * scripted, on the pattern the `Select` proved on 2026-07-31, superseding the
 * 2026-07-30 decision to keep the native popup. The argument that kept it is
 * the argument that lost it: the platform's calendar is good work, but it is
 * drawn by the operating system, no CSS reaches inside it, and the review
 * meeting reported the three things it does wrong in this product. It opened
 * on nothing rather than on the date already in the field; it stayed up after
 * a day was chosen; and its time half is a spinner nobody could read at a
 * glance. None of the three is fixable from here, so the popup is now ours.
 *
 * What is deliberately NOT replaced is the field itself. The control stays a
 * native `<input type="date">`, so typing keeps the segment grammar Windows
 * users already have, the value stays the browser's own ISO string, and the
 * closed field's arrow keys still step the segment under the caret. Only the
 * popup changed. Chrome 151 was measured on 2026-08-04 to confirm the OS
 * popup is now unreachable: neither a click in the field, nor Enter, Space,
 * F4 nor Alt+ArrowDown opens it, and the indicator that does is hidden in
 * `styles.css`.
 *
 * Everything here is drawn off ONE number, `PICKER_SCALE.cell`, in whole
 * pixels, for the reason the banner recorded on 2026-08-01: ratio arithmetic
 * reaching the rasteriser as fractions puts seams between boxes that should
 * meet, and a calendar is forty-two boxes that must line up with seven
 * headings.
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

export type PickerType = "date" | "time" | "datetime-local"

/**
 * The popup's geometry, in WHOLE PIXELS.
 *
 * `cell` is the one number chosen; everything else is a ratio of it, rounded
 * once, here, and pinned by `date-picker.test.tsx`. 36px is the day cell: it
 * clears WCAG 2.2's 24px target-size floor with room to spare while keeping
 * the whole grid narrower than the fields that carry it. It is deliberately
 * not the product's 44px button minimum, because a 44px cell makes the grid
 * 320px wide, which is wider than the wizard's own two-column fields.
 *
 * `height` and `width` are the envelope the flip measurement uses, and the
 * flip is what keeps a field at the foot of the employee dialog usable.
 */
export const PICKER_SCALE = {
  /** The day cell, square. The base. */
  cell: 36,
  /** Between cells: 0.06 x cell. */
  gap: 2,
  /** The popup's own padding: 0.22 x cell. */
  pad: 8,
  /** The weekday heading row: 0.55 x cell. */
  weekday: 20,
  /** Always six, so the popup does not change height from month to month. */
  weeks: 6,
  /** The month's rows: weeks x cell + the gaps between them. */
  grid: 226,
  /**
   * The whole popup: pad + nav row + the gap under it + the weekday row + the
   * gap under that + the grid + pad, and the two border pixels. Measured
   * against the rendered popup on 2026-08-04 and equal to the pixel.
   */
  height: 304,
  /** pad + seven cells + six gaps + pad, and the two border pixels. */
  width: 282,
  /** The time column beside the calendar: two cells and one gap. */
  time: 74,
  /** A month-stepping button: 0.78 x cell. */
  nav: 28,
} as const

/** The date popup and the time column side by side. */
const WIDE = PICKER_SCALE.width + PICKER_SCALE.pad + PICKER_SCALE.time

/** How wide the popup will be, which is what decides if it must right-align. */
export function popupWidth(type: PickerType): number {
  if (type === "date") return PICKER_SCALE.width
  if (type === "time") return PICKER_SCALE.pad * 2 + PICKER_SCALE.time + 2
  return WIDE
}

// --- The calendar's own vocabulary ------------------------------------------
//
// Written out rather than taken from `toLocaleString`. The house rule is that
// a promised format is hand-built to the domain's convention and never varies
// with the browser's locale, and it applies to a month name exactly as it
// applies to money: this product is international English, and a machine set
// to Afrikaans must not render "Augustus" inside an English screen.

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const

/**
 * Monday first, not Sunday.
 *
 * The product schedules training, which is booked against a working week, and
 * a week that starts on Monday keeps Saturday and Sunday together at its end
 * where a reader can see the weekend as one block. It is also the ISO order.
 */
const WEEKDAYS = [
  ["Mon", "Monday"],
  ["Tue", "Tuesday"],
  ["Wed", "Wednesday"],
  ["Thu", "Thursday"],
  ["Fri", "Friday"],
  ["Sat", "Saturday"],
  ["Sun", "Sunday"],
] as const

const pad2 = (value: number) => String(value).padStart(2, "0")

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * Day arithmetic on the UTC calendar, never on a local instant.
 *
 * A calendar day has no instant: adding 24 hours to a local `Date` across a
 * daylight-saving boundary lands on the same day or skips one, depending on
 * the viewer's zone. `Date.UTC` has no such boundary, and since the value is
 * only ever read back as year, month and day, the zone never enters the
 * arithmetic. It is the same reasoning that keeps a `DateField` a date on the
 * server rather than an offset.
 */
function isoOf(year: number, month: number, day: number): string {
  const at = new Date(Date.UTC(year, month, day))
  return `${at.getUTCFullYear()}-${pad2(at.getUTCMonth() + 1)}-${pad2(at.getUTCDate())}`
}

function partsOf(iso: string) {
  const [year, month, day] = iso.split("-").map(Number)
  return { year: year ?? 1970, month: (month ?? 1) - 1, day: day ?? 1 }
}

function addDays(iso: string, days: number): string {
  const { year, month, day } = partsOf(iso)
  return isoOf(year, month, day + days)
}

/** Clamped to the target month's length, so 31 January plus a month is 28 or 29 February. */
function addMonths(iso: string, months: number): string {
  const { year, month, day } = partsOf(iso)
  const last = new Date(Date.UTC(year, month + months + 1, 0)).getUTCDate()
  return isoOf(year, month + months, Math.min(day, last))
}

/** Today on the viewer's own wall clock, which is the only "today" a user has. */
export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`
}

/** Where the week holding this day starts, Monday. */
function weekStart(iso: string): string {
  const { year, month, day } = partsOf(iso)
  const index = (new Date(Date.UTC(year, month, day)).getUTCDay() + 6) % 7
  return addDays(iso, -index)
}

/** Six rows of seven days, the month's own days plus the days that fill the corners. */
function weeksOf(iso: string): string[][] {
  const { year, month } = partsOf(iso)
  const start = weekStart(isoOf(year, month, 1))
  return Array.from({ length: PICKER_SCALE.weeks }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  )
}

function monthLabel(iso: string): string {
  const { year, month } = partsOf(iso)
  return `${MONTHS[month]} ${year}`
}

function dayLabel(iso: string): string {
  const { year, month, day } = partsOf(iso)
  return `${day} ${MONTHS[month]} ${year}`
}

// --- Reading and writing the field's value ----------------------------------
//
// The value is the browser's own: `YYYY-MM-DD`, `HH:MM`, or the two joined by
// a T. Split and rejoin rather than parsed into a `Date` and formatted back,
// because a round trip through an instant is exactly where a timezone gets to
// move somebody's date by a day.

export function dayOf(value: string, type: PickerType): string {
  if (type === "time") return ""
  const day = value.slice(0, 10)
  return DAY_PATTERN.test(day) ? day : ""
}

export function timeOf(value: string, type: PickerType): string {
  const time = type === "time" ? value.slice(0, 5) : value.slice(11, 16)
  return TIME_PATTERN.test(time) ? time : ""
}

/**
 * The field's new value once a day has been chosen.
 *
 * On a datetime field with nothing in it yet this deliberately reports
 * midnight, which is what the browser's own picker did. The wizard reads that
 * exact string to move a freshly picked start to 08:00, the hour the client's
 * own records carry, and that rule lives at the call site where it belongs.
 */
export function withDay(value: string, type: PickerType, day: string): string {
  if (type !== "datetime-local") return day
  return `${day}T${timeOf(value, type) || "00:00"}`
}

/** The field's new value once a time has been chosen, or "" if no day is set yet. */
export function withTime(value: string, type: PickerType, time: string): string {
  if (type !== "datetime-local") return time
  const day = dayOf(value, type)
  return day ? `${day}T${time}` : ""
}

/**
 * The times offered, every `minutes` from midnight.
 *
 * A value that falls between the steps is inserted in its place rather than
 * dropped, because a popup that cannot show the value the field holds is the
 * defect this work exists to fix. Any minute at all can still be typed.
 */
export function timeSlots(minutes: number, current: string): string[] {
  const slots: string[] = []
  for (let at = 0; at < 24 * 60; at += minutes) {
    slots.push(`${pad2(Math.floor(at / 60))}:${pad2(at % 60)}`)
  }
  if (TIME_PATTERN.test(current) && !slots.includes(current)) {
    const after = slots.findIndex((slot) => slot > current)
    slots.splice(after < 0 ? slots.length : after, 0, current)
  }
  return slots
}

/** The slot the list should stand on when the field holds nothing: this hour. */
function slotNear(slots: string[], now = new Date()): number {
  const wall = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`
  const after = slots.findIndex((slot) => slot > wall)
  return after <= 0 ? 0 : after - 1
}

// --- The popup ---------------------------------------------------------------

const NAV = "grid place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"

/**
 * The calendar, the time list, or both, hanging off the field.
 *
 * A child of the field and never a portal: a native `<dialog>` sits in the
 * browser's top layer, and the employee form is one, so anything portalled to
 * the body would paint underneath the form it belongs to. Being a child is
 * also what makes the flip measurement necessary, and `popup-placement.ts`
 * carries it.
 *
 * `role="dialog"` without `aria-modal`: focus moves in, but it is not trapped.
 * Tab leaves, which is the grammar every other control in this product has.
 */
export function PickerPopup({
  type,
  value,
  minutes,
  dropUp,
  alignEnd,
  onPick,
  onDismiss,
}: {
  type: PickerType
  /** Exactly what the input holds. */
  value: string
  /** The step between offered times, in minutes. */
  minutes: number
  dropUp: boolean
  alignEnd: boolean
  /** The whole new value for the field. Commits and closes. */
  onPick: (next: string) => void
  /** Escape, tab-out: nothing committed, focus back on the field. */
  onDismiss: () => void
}) {
  const wantsCalendar = type !== "time"
  const wantsTime = type !== "date"

  const day = dayOf(value, type)
  const time = timeOf(value, type)
  const today = todayIso()

  // Where the keyboard stands. The calendar's is a day rather than an index,
  // so walking off the end of a month simply names a day in the next one and
  // the view follows it.
  const [cursor, setCursor] = React.useState(() => day || today)
  const [place, setPlace] = React.useState<"calendar" | "time">(
    wantsCalendar ? "calendar" : "time",
  )

  const slots = React.useMemo(() => timeSlots(minutes, time), [minutes, time])
  const [slot, setSlot] = React.useState(() => {
    const found = slots.indexOf(time)
    return found >= 0 ? found : slotNear(slots)
  })

  // A datetime's time cannot be chosen before its day, because a time on its
  // own would have to invent a date, and inventing data the user did not give
  // is worse than asking for the day first.
  const timeReady = type !== "datetime-local" || Boolean(day)

  const rootRef = React.useRef<HTMLDivElement>(null)
  const gridRef = React.useRef<HTMLTableElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const listId = React.useId()
  const optionId = (index: number) => `${listId}-time-${index}`

  // Brings the popup into view inside a modal's scrolling body, which is the
  // second half of the flip: choosing the roomier side does not help if that
  // side is still a few pixels short, and the employee form's later date
  // fields sit exactly there. Optional call, since jsdom has no layout.
  React.useEffect(() => {
    rootRef.current?.scrollIntoView?.({ block: "nearest" })
  }, [])

  // Focus follows the keyboard's own position: onto the day being walked, or
  // onto the list, whose active option is named by `aria-activedescendant`
  // rather than by moving focus into it. Same split the Select uses.
  React.useEffect(() => {
    if (place !== "calendar") return
    gridRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.focus()
  }, [cursor, place])

  React.useEffect(() => {
    if (place !== "time") return
    listRef.current?.focus()
  }, [place])

  // On the way open the current time is centred, so the times either side of
  // it are in view and the list reads as a place in the day rather than as a
  // list that happens to end there. After that a walked option is only brought
  // to the nearest edge, which is what stops the list jumping under the
  // keyboard. The scroll is set on the list itself rather than through
  // `scrollIntoView`, which would also scroll the page the popup sits on.
  const centred = React.useRef(false)
  React.useEffect(() => {
    const list = listRef.current
    const option = list?.querySelector<HTMLElement>('[data-active="true"]')
    if (!list || !option) return
    if (centred.current) {
      option.scrollIntoView?.({ block: "nearest" })
      return
    }
    centred.current = true
    list.scrollTop = option.offsetTop - (list.clientHeight - option.offsetHeight) / 2
  }, [slot])

  function takeDay(iso: string) {
    onPick(withDay(value, type, iso))
  }

  function takeTime(index: number) {
    const chosen = slots[index]
    if (!chosen || !timeReady) return
    const next = withTime(value, type, chosen)
    if (next) onPick(next)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const { key, shiftKey, altKey, ctrlKey, metaKey } = event

    if (key === "Escape" || (altKey && key === "ArrowUp")) {
      event.preventDefault()
      onDismiss()
      return
    }

    // Tab crosses to the popup's other half if there is one, and otherwise
    // closes and stands back on the field, which is the ruled listbox
    // behaviour: tab commits nothing. The month buttons are out of the tab
    // order on purpose, the same as the field's own icon; the page keys reach
    // every month a mouse can.
    if (key === "Tab") {
      event.preventDefault()
      if (place === "calendar" && !shiftKey && wantsTime && timeReady) setPlace("time")
      else if (place === "time" && shiftKey && wantsCalendar) setPlace("calendar")
      else onDismiss()
      return
    }

    if (ctrlKey || metaKey) return

    if (place === "time") {
      const last = slots.length - 1
      const step = (by: number) => {
        event.preventDefault()
        setSlot((at) => Math.min(Math.max(at + by, 0), last))
      }
      switch (key) {
        case "ArrowDown":
          return step(1)
        case "ArrowUp":
          return step(-1)
        case "PageDown":
          return step(Math.round(60 / minutes))
        case "PageUp":
          return step(-Math.round(60 / minutes))
        case "Home":
          event.preventDefault()
          return setSlot(0)
        case "End":
          event.preventDefault()
          return setSlot(last)
        case "Enter":
        case " ":
          event.preventDefault()
          return takeTime(slot)
        default:
          return
      }
    }

    const walk = (next: string) => {
      event.preventDefault()
      setCursor(next)
    }
    switch (key) {
      case "ArrowLeft":
        return walk(addDays(cursor, -1))
      case "ArrowRight":
        return walk(addDays(cursor, 1))
      case "ArrowUp":
        return walk(addDays(cursor, -7))
      case "ArrowDown":
        return walk(addDays(cursor, 7))
      case "Home":
        return walk(weekStart(cursor))
      case "End":
        return walk(addDays(weekStart(cursor), 6))
      case "PageUp":
        return walk(addMonths(cursor, shiftKey ? -12 : -1))
      case "PageDown":
        return walk(addMonths(cursor, shiftKey ? 12 : 1))
      case "Enter":
      case " ":
        event.preventDefault()
        return takeDay(cursor)
      default:
        return
    }
  }

  const label =
    type === "time"
      ? "Choose a time"
      : type === "datetime-local"
        ? "Choose a date and time"
        : "Choose a date"

  const month = partsOf(cursor).month

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={label}
      data-picker-popup=""
      onKeyDown={onKeyDown}
      style={
        {
          "--picker-cell": `${PICKER_SCALE.cell}px`,
          "--picker-gap": `${PICKER_SCALE.gap}px`,
          "--picker-pad": `${PICKER_SCALE.pad}px`,
          "--picker-grid": `${PICKER_SCALE.grid}px`,
          "--picker-nav": `${PICKER_SCALE.nav}px`,
          "--picker-weekday": `${PICKER_SCALE.weekday}px`,
          "--picker-time": `${PICKER_SCALE.time}px`,
          "--picker-column": `${7 * PICKER_SCALE.cell + 6 * PICKER_SCALE.gap}px`,
        } as React.CSSProperties
      }
      className={cn(
        // The Select's own popup idioms, so the two read as siblings: the
        // popover surface, its border and elevation, and the foreground
        // declared rather than inherited, because a `<dialog>` hands down the
        // user agent's `CanvasText` and three of these fields live in one.
        "absolute z-50 flex gap-[var(--picker-pad)] rounded-lg border bg-popover p-[var(--picker-pad)] text-popover-foreground shadow-elevated",
        dropUp ? "bottom-full mb-1" : "top-full mt-1",
        alignEnd ? "right-0" : "left-0",
      )}
    >
      {wantsCalendar && (
        <div className="w-[var(--picker-column)]">
          <div className="mb-[var(--picker-gap)] flex h-[var(--picker-cell)] items-center justify-between">
            <div className="flex">
              <button
                type="button"
                tabIndex={-1}
                aria-label="Previous year"
                className={cn(NAV, "h-[var(--picker-nav)] w-[var(--picker-nav)]")}
                onClick={() => setCursor(addMonths(cursor, -12))}
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                tabIndex={-1}
                aria-label="Previous month"
                className={cn(NAV, "h-[var(--picker-nav)] w-[var(--picker-nav)]")}
                onClick={() => setCursor(addMonths(cursor, -1))}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {/* Announced when it changes, so a page key is not a silent move
                for anyone who cannot see the grid redraw. */}
            <div aria-live="polite" className="font-display text-sm font-bold">
              {monthLabel(cursor)}
            </div>
            <div className="flex">
              <button
                type="button"
                tabIndex={-1}
                aria-label="Next month"
                className={cn(NAV, "h-[var(--picker-nav)] w-[var(--picker-nav)]")}
                onClick={() => setCursor(addMonths(cursor, 1))}
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                tabIndex={-1}
                aria-label="Next year"
                className={cn(NAV, "h-[var(--picker-nav)] w-[var(--picker-nav)]")}
                onClick={() => setCursor(addMonths(cursor, 12))}
              >
                <ChevronsRight className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>

          {/* Roles are written out rather than left implicit. A `<td>` inside
              a `role="grid"` is a gridcell by inheritance, but the mapping is
              context-dependent and this grid's whole keyboard contract rests
              on it, so it is stated. The negative margin cancels the outer
              ring of border-spacing, which is what makes the rendered height
              the `grid` number above rather than two gaps more. */}
          <table
            ref={gridRef}
            role="grid"
            aria-label={monthLabel(cursor)}
            style={{ margin: "calc(var(--picker-gap) * -1)" }}
            className="border-separate border-spacing-[var(--picker-gap)]"
          >
            <thead>
              <tr role="row">
                {WEEKDAYS.map(([short, full]) => (
                  <th
                    key={short}
                    role="columnheader"
                    scope="col"
                    abbr={full}
                    className="h-[var(--picker-weekday)] w-[var(--picker-cell)] text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    {short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeksOf(cursor).map((week) => (
                <tr key={week[0]} role="row">
                  {week.map((iso) => {
                    const parts = partsOf(iso)
                    const outside = parts.month !== month
                    const chosen = iso === day
                    const active = iso === cursor
                    return (
                      <td
                        key={iso}
                        role="gridcell"
                        aria-label={dayLabel(iso)}
                        aria-selected={chosen}
                        tabIndex={active ? 0 : -1}
                        data-active={active ? "true" : undefined}
                        data-today={iso === today ? "true" : undefined}
                        onClick={() => takeDay(iso)}
                        className={cn(
                          "h-[var(--picker-cell)] w-[var(--picker-cell)] cursor-pointer rounded-md text-center align-middle text-sm tabular-nums",
                          outside ? "text-muted-foreground" : "text-foreground",
                          chosen
                            ? "bg-primary font-medium text-primary-foreground"
                            : "hover:bg-muted",
                          // Today wears a ring rather than a fill, so the day
                          // chosen and the day it is stay two different
                          // statements and can both be true at once.
                          iso === today && !chosen && "ring-1 ring-inset ring-border-strong",
                        )}
                      >
                        {parts.day}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wantsTime && (
        <div
          className="flex w-[var(--picker-time)] flex-col"
          // Beside a calendar the column is exactly as tall as the calendar
          // is, so the list and the hint under it share that height instead
          // of the hint making the popup taller than the envelope the flip
          // was measured against. Alone, the list sizes itself.
          style={
            wantsCalendar
              ? {
                  height:
                    "calc(var(--picker-cell) + var(--picker-gap) + var(--picker-weekday) + var(--picker-gap) + var(--picker-grid))",
                }
              : undefined
          }
        >
          {wantsCalendar && (
            <div className="mb-[var(--picker-gap)] flex h-[var(--picker-cell)] items-center justify-center text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Time
            </div>
          )}
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Time"
            aria-disabled={timeReady ? undefined : true}
            aria-activedescendant={timeReady ? optionId(slot) : undefined}
            tabIndex={timeReady ? 0 : -1}
            // The list ends where the calendar's last row ends. Derived, not
            // eyeballed, and the derivation was checked in the rendered app:
            // beside a calendar the list stands under its own heading, and
            // the calendar's rows plus its weekday heading and the one gap
            // between them is what it has to match. The first version added
            // a gap too many and put the popup 2px out, which is exactly the
            // fraction-of-a-pixel drift the banner's whole-pixel rule exists
            // to stop. Alone, the list is simply the grid's own height.
            style={wantsCalendar ? undefined : { maxHeight: "var(--picker-grid)" }}
            className={cn(
              "overflow-y-auto rounded-md",
              wantsCalendar && "min-h-0 flex-1",
              !timeReady && "opacity-50",
            )}
          >
            {slots.map((option, index) => {
              const chosen = option === time
              return (
                <li
                  key={option}
                  id={optionId(index)}
                  role="option"
                  aria-selected={chosen}
                  data-active={index === slot ? "true" : undefined}
                  // Focus must not leave the list: it is what carries
                  // `aria-activedescendant`, exactly as the Select's trigger
                  // does.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => takeTime(index)}
                  className={cn(
                    "flex h-[var(--picker-cell)] items-center justify-center rounded-md text-sm tabular-nums",
                    timeReady ? "cursor-pointer" : "cursor-not-allowed",
                    chosen
                      ? "bg-primary font-medium text-primary-foreground"
                      : timeReady && "hover:bg-muted data-[active=true]:bg-muted",
                  )}
                >
                  {option}
                </li>
              )
            })}
          </ul>
          {!timeReady && (
            // Three words, because the column is two cells wide and the
            // rendered app showed "Choose a day first" wrapping to a second
            // line that the popup's own height then clipped. Beside a
            // calendar with its times greyed out, this says enough.
            <p className="mt-[var(--picker-gap)] text-center text-[11px] leading-tight text-muted-foreground">
              Choose a day
            </p>
          )}
        </div>
      )}
    </div>
  )
}
